// Monetisation (contracts/phase2.md §1, §12): paywall impressions by trigger
// source, purchases by product (lifetime counted apart from subscriptions), Pro
// players now by plan, and conversion by source over a date range. Paywall 2
// (contracts/paywall.md §2, §5) adds the app_open and exit_offer triggers and the
// conversion of each experiment variant shown in the range.

import { Link, useSearchParams } from "react-router";
import { usePaywall } from "../api/queries";
import { PRO_PRODUCTS, TRIGGER_SOURCES, type Paywall } from "../api/schemas";
import { BarChart, type BarDatum } from "../components/charts/charts";
import chartStyles from "../components/charts/charts.module.css";
import { Button, Card, EmptyState, ErrorState, Loading, PageHeader, Segmented, Stat, TextInput } from "../components/ui";
import { addDays, daysBetween, formatShortDate, todayUtc } from "../lib/dates";
import { fmtInt, fmtPct } from "../lib/format";
import { LIFETIME_PRODUCT, PLAN_LABEL, PRO_PLANS, PRODUCT_LABEL } from "../lib/pro";
import styles from "./pages.module.css";

const SOURCE_LABEL: Record<string, string> = {
  stats: "Stats",
  archive: "Archive",
  hardmode: "Hard Mode",
  freeze: "Freezes",
  levels: "Levels",
  badge: "Badges",
  settings: "Settings",
  onboarding: "Onboarding",
  app_open: "App open",
  exit_offer: "Exit offer",
};

const PRESETS = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
] as const;

/** Known keys first in contract order, then anything else the API reports. */
function ordered(known: readonly string[], present: string[]): string[] {
  return [...known, ...present.filter((k) => !known.includes(k)).sort()];
}

export function MonetisationPage() {
  const [params, setParams] = useSearchParams();
  const today = todayUtc();
  const to = params.get("to") || today;
  const from = params.get("from") || addDays(to, -29);
  const span = daysBetween(from, to) + 1;
  const preset = to === today ? PRESETS.find((p) => Number(p.value) === span)?.value : undefined;
  const q = usePaywall(from, to);
  const set = (f: string, t: string) => setParams(t === today && daysBetween(f, t) === 29 ? {} : { from: f, to: t }, { replace: true });

  const header = (
    <PageHeader
      title="Monetisation"
      meta={
        <span>
          Paywall impressions, purchases and conversion, {formatShortDate(from)} – {formatShortDate(to)} (UTC). Automatic triggers are capped
          at one a week per player and never shown around the daily; Settings is user-initiated and never refused.
        </span>
      }
      actions={
        <>
          <Segmented
            label="Range"
            value={preset ?? "custom"}
            options={[...PRESETS, ...(preset ? [] : [{ value: "custom", label: "Custom" }])]}
            onChange={(v) => v !== "custom" && set(addDays(today, -(Number(v) - 1)), today)}
          />
          <TextInput type="date" value={from} max={to} onChange={(e) => e.target.value && set(e.target.value, to)} aria-label="From" />
          <TextInput type="date" value={to} min={from} max={today} onChange={(e) => e.target.value && set(from, e.target.value)} aria-label="To" />
          {params.toString() ? (
            <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>
              Reset
            </Button>
          ) : null}
        </>
      }
    />
  );

  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  return (
    <>
      {header}
      <PaywallView data={q.data} fetching={q.isFetching} />
    </>
  );
}

function PaywallView({ data, fetching }: { data: Paywall; fetching: boolean }) {
  const sources = ordered(TRIGGER_SOURCES, Object.keys({ ...data.impressions.bySource, ...data.conversionBySource }));
  const products = ordered(PRO_PRODUCTS, Object.keys(data.purchases.byProduct));
  const shown = data.impressions.total ?? Object.values(data.impressions.bySource).reduce((a, b) => a + b.shown, 0);
  const capped = Object.values(data.impressions.bySource).reduce<number | null>((a, b) => (b.suppressed === null ? a : (a ?? 0) + b.suppressed), null);
  const bought = data.purchases.total ?? Object.values(data.purchases.byProduct).reduce((a, b) => a + b.count, 0);
  const { subscriptions, lifetime } = data.purchases;
  const trials = Object.values(data.purchases.byProduct).reduce<number | null>((a, b) => (b.trials === null ? a : (a ?? 0) + b.trials), null);

  const impressions: BarDatum[] = sources
    .filter((s) => data.impressions.bySource[s])
    .map((s) => {
      const v = data.impressions.bySource[s]!;
      return { key: s, label: SOURCE_LABEL[s] ?? s, value: v.shown, extra: v.suppressed, note: v.suppressed !== null ? `${fmtPct(v.suppressed / Math.max(1, v.shown + v.suppressed))} of requests refused` : undefined };
    });
  const purchases: BarDatum[] = products
    .filter((p) => data.purchases.byProduct[p])
    .map((p) => {
      const v = data.purchases.byProduct[p]!;
      const note = p === LIFETIME_PRODUCT ? "one-time purchase, never renews" : v.trials !== null ? `${fmtInt(v.trials)} started with the free trial` : undefined;
      return { key: p, label: PRODUCT_LABEL[p] ?? p, value: v.count, note };
    });
  const active = data.activeByPlan;
  const byPlan: BarDatum[] = active
    ? PRO_PLANS.map((plan) => ({
        key: plan,
        label: PLAN_LABEL[plan],
        value: active[plan],
        note: plan === "lifetime" ? "never expires; wins over a subscription" : plan === "grant" ? "support grants, time-limited" : undefined,
      }))
    : [];
  const conversion: BarDatum[] = sources
    .filter((s) => data.conversionBySource[s]?.rate !== null && data.conversionBySource[s] !== undefined)
    .map((s) => {
      const c = data.conversionBySource[s]!;
      return {
        key: s,
        label: SOURCE_LABEL[s] ?? s,
        value: c.rate ?? 0,
        note: c.impressions !== null && c.purchases !== null ? `${fmtInt(c.purchases)} purchases after ${fmtInt(c.impressions)} paywalls` : undefined,
      };
    })
    .sort((a, b) => b.value - a.value);

  // Overall conversion: attributed purchases ÷ paywalls shown when the API attributes them, else all purchases.
  const convRows = Object.values(data.conversionBySource);
  const attributed = convRows.length && convRows.every((c) => c.purchases !== null) ? convRows.reduce((a, c) => a + (c.purchases ?? 0), 0) : null;
  return (
    <div aria-busy={fetching} data-testid="monetisation">
      <div className={styles.statStrip}>
        <Stat label="Impressions" value={fmtInt(shown)} sub="paywalls shown" />
        <Stat label="Refused" value={capped === null ? "—" : fmtInt(capped)} sub="weekly cap or rules" />
        <Stat label="Purchases" value={fmtInt(bought)} sub="new App Store purchases" />
        <Stat label="Subscriptions" value={fmtInt(subscriptions)} sub="monthly + yearly" />
        <Stat label="Lifetime" value={fmtInt(lifetime)} sub="one-time, counted apart" />
        {trials !== null ? <Stat label="Free trials" value={fmtInt(trials)} sub="yearly, 1 week" /> : null}
        <Stat
          label="Conversion"
          value={fmtPct(shown ? (attributed ?? bought) / shown : null, 1)}
          sub={attributed !== null ? "purchases within 24 h ÷ shown" : "purchases ÷ impressions"}
        />
      </div>
      <div className={styles.moneyGrid}>
        <Card
          title="Impressions by trigger source"
          className={active ? styles.moneyTall : undefined}
          actions={
            capped !== null && impressions.some((r) => r.value > 0 || (r.extra ?? 0) > 0) ? (
              <span className={styles.legend}>
                <span>
                  <i className={chartStyles.swatch} /> shown
                </span>
                <span>
                  <i className={chartStyles.swatch} data-kind="extra" /> refused
                </span>
              </span>
            ) : null
          }
        >
          {impressions.some((r) => r.value > 0 || (r.extra ?? 0) > 0) ? (
            <BarChart rows={impressions} label="Paywall impressions by trigger source" valueName="shown" extraName="refused" labelWidth={96} />
          ) : (
            <EmptyState title="No impressions">No paywall was requested in this range.</EmptyState>
          )}
        </Card>
        <Card title="Purchases by product" actions={<span className="mono">{fmtInt(bought)} total</span>}>
          {purchases.some((r) => r.value > 0) ? (
            <BarChart rows={purchases} label="Purchases by product" valueName="purchases" tone="accent" labelWidth={116} />
          ) : (
            <EmptyState title="No purchases">Nothing was bought in this range.</EmptyState>
          )}
        </Card>
        {active ? (
          <Card title="Pro players now by plan" actions={<span className="mono">{fmtInt(active.total)} Pro</span>}>
            {active.total > 0 ? (
              <BarChart rows={byPlan} label="Pro players now by plan" valueName="players" tone="accent" labelWidth={116} />
            ) : (
              <EmptyState title="No Pro players">Nobody has Pro right now.</EmptyState>
            )}
          </Card>
        ) : null}
        <Card title="Conversion by source" actions={<span className="monoLabel">purchases within 24 h ÷ paywalls shown</span>} className={styles.moneyWide}>
          {conversion.length ? (
            <BarChart
              rows={conversion}
              label="Conversion rate by trigger source"
              valueName="converted"
              tone="success"
              format={(v) => fmtPct(v, 1)}
              labelWidth={96}
              integerTicks={false}
            />
          ) : (
            <EmptyState title="No conversion data">Conversion shows once paywalls have been seen in this range.</EmptyState>
          )}
        </Card>
        {data.byVariant?.length ? (
          <Card
            title="Conversion by paywall variant"
            actions={<Link to="/monetisation/experiments">Experiments →</Link>}
            className={styles.moneyWide}
          >
            <div data-testid="by-variant">
              <BarChart
                rows={data.byVariant.map((v) => ({
                  key: `${v.experimentId}-${v.variant}`,
                  label: `${v.name} · ${v.variant.toUpperCase()}`,
                  value: v.conversion ?? 0,
                  note: `${fmtInt(v.purchasers)} purchasers of ${fmtInt(v.players)} players shown · ${fmtInt(v.shown)} paywalls`,
                }))}
                label="Conversion rate by paywall variant"
                valueName="converted"
                tone="success"
                format={(v) => fmtPct(v, 1)}
                labelWidth={220}
                integerTicks={false}
              />
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
